import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import Confetti from '../Confetti';
import { DecisionIcon, actionTone } from './ReviewUi';

type Decision = 'use' | 'maybe' | 'remove';

const SCENE_DESIGN_WIDTH = 592;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface ScenePlayback {
  reducedMotion: boolean;
  hasPlayed: boolean;
  animationClass: string;
  play: () => void;
}

function useScenePlayback(): ScenePlayback {
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event: MediaQueryListEvent) => {
      setReducedMotion(event.matches);
      if (event.matches) setHasPlayed(false);
    };

    setReducedMotion(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }

    return undefined;
  }, []);

  return {
    reducedMotion,
    hasPlayed,
    animationClass: reducedMotion
      ? hasPlayed ? 'tour-scene-play-once' : ''
      : 'tour-scene-loop',
    play: () => setHasPlayed(true),
  };
}

function SceneCursor({ className }: { className: string }) {
  return (
    <span className={`tour-scene-cursor tour-scene-animated ${className}`}>
      <svg viewBox="0 0 14 20" aria-hidden="true">
        <path d="M1 1 L13 11 L7.5 12 L10 18.5 L7.5 19.5 L5 13 L1 16 Z" fill="#1b201e" stroke="#fff" strokeWidth="1" />
      </svg>
    </span>
  );
}

function SceneFrame({
  step,
  label,
  playback,
  onPlay,
  children,
}: {
  step: number;
  label: string;
  playback: ScenePlayback;
  onPlay?: () => void;
  children: ReactNode;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [sceneScale, setSceneScale] = useState(1);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return undefined;

    const frame = frameRef.current;
    if (!frame) return undefined;

    const updateScale = () => {
      if (frame.clientWidth > 0) {
        setSceneScale(frame.clientWidth / SCENE_DESIGN_WIDTH);
      }
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={frameRef}
      className={`tour-scene tour-scene-step-${step} mt-4 aspect-[16/10] overflow-hidden rounded-input border border-line bg-elevated`}
      data-testid={`onboarding-tour-scene-step-${step}`}
      role="img"
      aria-label={label}
    >
      <div
        className={`tour-scene-artwork ${playback.animationClass}`}
        style={{ '--scene-scale': sceneScale } as CSSProperties}
        aria-hidden="true"
      >
        {children}
      </div>
      {playback.reducedMotion && !playback.hasPlayed ? (
        <button
          type="button"
          className="tour-scene-play"
          onClick={() => {
            playback.play();
            onPlay?.();
          }}
        >
          Play scene
        </button>
      ) : null}
    </div>
  );
}

function MiniVideo({ animated = false }: { animated?: boolean }) {
  return (
    <div className="tour-scene-video">
      <div className="player-chrome tour-scene-player-chrome">
        <span className="tour-scene-player-button">
          <span className={`tour-scene-play-icon ${animated ? 'tour-scene-2-play-icon tour-scene-animated' : ''}`} />
          {animated ? (
            <span className="tour-scene-pause-icon tour-scene-2-pause-icon tour-scene-animated">
              <i />
              <i />
            </span>
          ) : null}
        </span>
        <span className="tour-scene-player-track">
          <span className={`tour-scene-player-progress ${animated ? 'tour-scene-2-progress tour-scene-animated' : 'tour-scene-player-progress-static'}`} />
        </span>
      </div>
    </div>
  );
}

function MiniComments({ children }: { children?: ReactNode }) {
  return (
    <div className="tour-scene-comments">
      <span className="tour-scene-comments-heading">Comments</span>
      <span className="tour-scene-comments-input">Add a comment…</span>
      {children}
    </div>
  );
}

function ReviewLayout({
  transcript,
  comments,
  animatedVideo = false,
}: {
  transcript: ReactNode;
  comments?: ReactNode;
  animatedVideo?: boolean;
}) {
  return (
    <div className="tour-scene-review-grid">
      <div className="tour-scene-transcript-card">{transcript}</div>
      <div className="tour-scene-review-side">
        <MiniVideo animated={animatedVideo} />
        <MiniComments>{comments}</MiniComments>
      </div>
    </div>
  );
}

function TranscriptHeading({ title }: { title: string }) {
  return (
    <>
      <span className="tour-scene-eyebrow">Interview 1</span>
      <div className="tour-scene-title">{title}</div>
    </>
  );
}

function SceneDecision({ action }: { action: Decision }) {
  const label = action === 'use' ? 'Use' : action === 'maybe' ? 'Maybe' : 'Remove';
  return (
    <span className={`tour-scene-decision ${actionTone[action]} ${action === 'use' ? 'tour-scene-3-use-action tour-scene-animated' : ''}`}>
      <DecisionIcon action={action} className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

function SelectChip({ action }: { action: 'use' | 'maybe' }) {
  const label = action === 'use' ? 'Use' : 'Maybe';
  return (
    <span className={`tour-scene-select-chip ${actionTone[action]}`}>
      <DecisionIcon action={action} className="h-2 w-2" />
      {label}
    </span>
  );
}

function Step2Scene() {
  const playback = useScenePlayback();
  return (
    <SceneFrame step={2} label="Animated demonstration of the transcript, player and comments layout" playback={playback}>
      <div className="tour-scene-inset">
        <ReviewLayout
          animatedVideo
          transcript={(
            <>
              <TranscriptHeading title="Margot · Founder story" />
              <div className="tour-scene-cue">
                <span className="tour-scene-timestamp">02:14</span>
                <div>
                  <span className="tour-scene-speaker">Margot K</span>
                  <p className="tour-scene-copy">We started in a garage with one camera and a borrowed light.</p>
                </div>
              </div>
              <div className="tour-scene-cue tour-scene-2-jump-cue tour-scene-animated">
                <span className="tour-scene-timestamp tour-scene-timestamp-hot">02:41</span>
                <div>
                  <p className="tour-scene-copy">By the second year we had stopped calling it a side project.</p>
                </div>
              </div>
            </>
          )}
        />
        <SceneCursor className="tour-scene-2-cursor" />
      </div>
    </SceneFrame>
  );
}

function Step3Scene() {
  const playback = useScenePlayback();
  return (
    <SceneFrame step={3} label="Animated demonstration of marking a transcript selection" playback={playback}>
      <div className="tour-scene-inset">
        <div className="tour-scene-3-document">
          <TranscriptHeading title="Margot · Founder story" />
          <div className="tour-scene-3-cue">
            <span className="tour-scene-timestamp">02:14</span>
            <div>
              <span className="tour-scene-speaker">Margot K</span>
              <p className="tour-scene-3-copy">
                We started in a garage with one camera and a borrowed light.{' '}
                <span className="tour-scene-3-selection tour-scene-animated">What kept us going was the feeling that every client deserved a story told properly.</span>
              </p>
              <div className="tour-scene-3-chip-row">
                <span className="tour-scene-3-after tour-scene-animated">
                  <span className={`tour-scene-3-result-chip ${actionTone.use}`}>
                    <DecisionIcon action="use" className="h-2 w-2" />
                    Use · PS
                  </span>
                  <span className="tour-scene-change">Change</span>
                </span>
                <span className="tour-scene-3-paragraph-pill">
                  <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4 13-.8 3.8L7 16l8.5-8.5-3-3zM11.5 5.5l3 3" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" /></svg>
                  Mark this paragraph
                </span>
              </div>
            </div>
          </div>
          <span className="tour-scene-3-rail tour-scene-animated" />
        </div>
        <div className="tour-scene-popover tour-scene-3-popover tour-scene-animated">
          <span className="tour-scene-popover-caret" />
          <div className="tour-scene-popover-head">
            <span className="tour-scene-popover-label">Your selection</span>
            <span className="tour-scene-popover-close">×</span>
          </div>
          <p className="tour-scene-popover-quote">“What kept us going was the feeling that every client deserved…”</p>
          <div className="tour-scene-3-actions">
            <SceneDecision action="use" />
            <SceneDecision action="maybe" />
            <SceneDecision action="remove" />
          </div>
          <div className="tour-scene-3-comment-row">
            <span className="tour-scene-3-comment-field">Add a comment about this line…</span>
            <span className="tour-scene-3-save">Save comment</span>
          </div>
        </div>
        <SceneCursor className="tour-scene-3-cursor" />
      </div>
    </SceneFrame>
  );
}

function Step4Scene() {
  const playback = useScenePlayback();
  return (
    <SceneFrame step={4} label="Animated demonstration of saving a timestamped comment" playback={playback}>
      <div className="tour-scene-inset">
        <ReviewLayout
          comments={(
            <div className="tour-scene-4-comment tour-scene-animated">
              <div className="tour-scene-4-comment-top">
                <span className="tour-scene-4-name">Priya</span>
                <span className="tour-scene-4-time">Just now</span>
                <span className="tour-scene-4-anchor">Interview 1 · 07:48</span>
              </div>
              <p>Could we open the film with this line?</p>
            </div>
          )}
          transcript={(
            <>
              <TranscriptHeading title="Margot · Founder story" />
              <div className="tour-scene-cue">
                <span className="tour-scene-timestamp">07:32</span>
                <div>
                  <span className="tour-scene-speaker">Margot K</span>
                  <p className="tour-scene-copy">Everyone told us to wait another year before going out on our own.</p>
                </div>
              </div>
              <div className="tour-scene-cue">
                <span className="tour-scene-timestamp">07:48</span>
                <div>
                  <p className="tour-scene-copy"><span className="tour-scene-4-highlight">We nearly took the safe route instead, and I still think about that.</span></p>
                </div>
              </div>
            </>
          )}
        />
        <div className="tour-scene-popover tour-scene-4-popover tour-scene-animated">
          <span className="tour-scene-popover-caret" />
          <div className="tour-scene-popover-head">
            <span className="tour-scene-popover-label">Your selection</span>
            <span className="tour-scene-popover-close">×</span>
          </div>
          <p className="tour-scene-popover-quote">“We nearly took the safe route instead, and I still think…”</p>
          <div className="tour-scene-4-field">
            <span className="tour-scene-4-typed tour-scene-animated">Could we open the film with this line?</span>
            <span className="tour-scene-typed-caret tour-scene-animated" />
          </div>
          <div className="tour-scene-4-save-row">
            <span className="tour-scene-4-save tour-scene-animated">Save comment</span>
          </div>
        </div>
        <SceneCursor className="tour-scene-4-cursor" />
      </div>
    </SceneFrame>
  );
}

function Step5Scene() {
  const playback = useScenePlayback();
  return (
    <SceneFrame step={5} label="Animated demonstration of opening the Selects roll-up" playback={playback}>
      <div className="tour-scene-inset">
        <div className="tour-scene-5-document">
          <div className="tour-scene-5-header">
            <span className="tabs tabs-switch tour-scene-5-tabs">
              <span className="tab tab-active tour-scene-5-tab tour-scene-5-interviews-tab tour-scene-animated">Interviews</span>
              <span className="tab tour-scene-5-tab tour-scene-5-selects-tab tour-scene-animated">Selects <span className="tab-count">3</span></span>
            </span>
          </div>
          <div className="tour-scene-5-body">
            <div className="tour-scene-5-view tour-scene-5-interviews tour-scene-animated">
              <span className="tour-scene-5-skeleton w-2/5" />
              <span className="tour-scene-5-skeleton w-[85%]" />
              <span className="tour-scene-5-skeleton w-[70%]" />
              <span className="tour-scene-5-skeleton w-[85%]" />
              <span className="tour-scene-5-skeleton w-3/5" />
            </div>
            <div className="tour-scene-5-view tour-scene-5-selects tour-scene-animated">
              <span className="tour-scene-eyebrow">Coverage check</span>
              <div className="tour-scene-title">Project selects</div>
              <div className="tour-scene-5-group tour-scene-5-rise-1 tour-scene-animated">
                <span className="tour-scene-5-group-heading">Interview 1 · Margot · Founder story</span>
                <div className="tour-scene-5-item">
                  <span className="tour-scene-5-range">02:14 to 02:31</span>
                  <span className="tour-scene-5-quote">“Every client deserved a story told properly.”<small>Your select</small></span>
                  <SelectChip action="use" />
                </div>
                <div className="tour-scene-5-item">
                  <span className="tour-scene-5-range">07:48 to 07:59</span>
                  <span className="tour-scene-5-quote">“We nearly took the safe route instead.”<small>Your select</small></span>
                  <SelectChip action="maybe" />
                </div>
              </div>
              <div className="tour-scene-5-group tour-scene-5-rise-2 tour-scene-animated">
                <span className="tour-scene-5-group-heading">Interview 2 · Dev · The first hire</span>
                <div className="tour-scene-5-item">
                  <span className="tour-scene-5-range">01:12 to 01:26</span>
                  <span className="tour-scene-5-quote">“The work had outgrown the name.”<small>Your select</small></span>
                  <SelectChip action="use" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <SceneCursor className="tour-scene-5-cursor" />
      </div>
    </SceneFrame>
  );
}

function Step6Scene() {
  const playback = useScenePlayback();
  const [confettiBurst, setConfettiBurst] = useState<number | null>(null);

  useEffect(() => {
    if (playback.reducedMotion) return undefined;
    let burst = 0;
    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;
    const startCycle = () => {
      showTimer = setTimeout(() => {
        burst += 1;
        setConfettiBurst(burst);
      }, 7_280);
      hideTimer = setTimeout(() => setConfettiBurst(null), 11_500);
    };
    startCycle();
    const cycleTimer = setInterval(startCycle, 14_000);
    return () => {
      clearInterval(cycleTimer);
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [playback.reducedMotion]);

  return (
    <SceneFrame
      step={6}
      label="Animated demonstration of signing and submitting a review"
      playback={playback}
      onPlay={() => setConfettiBurst(1)}
    >
      <div className="tour-scene-6-form-wrap">
        <div className="tour-scene-6-dialog tour-scene-animated">
          <span className="tour-scene-eyebrow">Submit your review</span>
          <div className="tour-scene-6-heading">You&apos;re sending your picks to Henry.</div>
          <p className="tour-scene-6-subcopy">Nothing is final. You can keep making changes until the project closes.</p>
          <span className="tour-scene-6-label">Type your name to sign</span>
          <div className="tour-scene-6-input">
            <span className="tour-scene-6-typed tour-scene-animated">Priya Sharma</span>
            <span className="tour-scene-typed-caret tour-scene-animated" />
          </div>
          <div className="tour-scene-6-button-row">
            <span className="tour-scene-6-submit tour-scene-animated">Submit review</span>
            <span className="tour-scene-6-quiet">Keep reviewing</span>
          </div>
        </div>
      </div>
      <div className="tour-scene-6-done tour-scene-animated">
        <div>
          <div className="tour-scene-6-emoji">🎉</div>
          <div className="tour-scene-6-sent">Sent · Today, 2:28 pm</div>
          <div className="tour-scene-6-done-heading">That&apos;s a wrap, Priya.</div>
          <p className="tour-scene-6-done-copy">Your picks for &quot;Founder stories&quot; are with Henry. Nothing is final until the project closes.</p>
          <span className="tour-scene-6-done-button">Done</span>
        </div>
      </div>
      {confettiBurst !== null ? (
        <Confetti key={confettiBurst} scale={0.4} lifetimeFrames={110} />
      ) : null}
      <SceneCursor className="tour-scene-6-cursor" />
    </SceneFrame>
  );
}

export function TourScene({ step }: { step: number }) {
  if (step === 2) return <Step2Scene />;
  if (step === 3) return <Step3Scene />;
  if (step === 4) return <Step4Scene />;
  if (step === 5) return <Step5Scene />;
  return null;
}

export function SubmissionCeremonyScene() {
  return <Step6Scene />;
}
